import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'technology', 'sustainability', 'case-studies', 'industry-news'];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div data-testid="blog-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 data-testid="blog-title" className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6">
            NEWS & <span className="text-primary">INSIGHTS</span>
          </h1>
          <p data-testid="blog-subtitle" className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Latest updates, industry trends, and thought leadership from GreenSmart
          </p>
        </motion.div>

        {/* Category Filter */}
        <div data-testid="blog-filters" className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-filter-${cat}`}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-2 font-mono text-xs uppercase tracking-wider transition-all ${
                selectedCategory === cat
                  ? 'bg-primary text-black font-bold'
                  : 'bg-transparent border border-primary/50 text-primary hover:bg-primary/10'
              }`}
            >
              {cat.replace('-', ' ')}
            </button>
          ))}
        </div>

        {/* Blog Posts */}
        {loading ? (
          <div data-testid="loading-spinner" className="text-center py-24">
            <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPosts.length > 0 ? (
          <div data-testid="blog-posts-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-card-${post.slug}`}
                  className="block group h-full"
                >
                  <div className="glass p-6 h-full flex flex-col hover:bg-white/5 transition-all hover:glow-green">
                    {post.image_url && (
                      <img 
                        src={post.image_url} 
                        alt={post.title} 
                        className="w-full h-48 object-cover mb-4"
                      />
                    )}
                    
                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-3 py-1 border border-primary/30 text-primary text-xs font-mono uppercase tracking-wider">
                        {post.category}
                      </span>
                    </div>

                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors flex-grow">
                      {post.title}
                    </h3>

                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-white/5">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          <User size={14} className="mr-1" />
                          {post.author}
                        </span>
                        <span className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {new Date(post.published_at).toLocaleDateString()}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div data-testid="no-posts-message" className="text-center py-24">
            <p className="text-muted-foreground text-lg">No blog posts available yet. Check back soon!</p>
          </div>
        )}
      </div>
    </div>
  );
}
